import type { Manifest, ManifestReference, Reference } from '../api/authoring'
import { typeOf } from './manifest'

// The second edge, read the way a control has to draw it.
//
// **A reference is not a placement, so none of the composition model
// applies to it.** Nothing arranges one, no field of the holder spells
// its edge id, and the composer never descends through it — which is
// exactly why `composeDocument` says nothing about references and this
// is a separate reading rather than another region kind. A region is
// discovered from the *fields schema*, because an arrangement is a field;
// an edge is discovered from the *manifest's* declaration, because that
// is the only place it exists.
//
// So the manifest leads here and the document answers. An edge with
// nothing on it is still an edge and still drawn, the way a declared
// region with no children is: it is what tells an author that a Pattern
// takes a declaration and is missing one.

/**
 * The reference edges a type declares, sorted by name.
 *
 * Empty for the types that declare none, which is most of them. Absent
 * and empty mean the same thing to a caller drawing a list, so the
 * distinction the contract keeps — associations are not this type's
 * concept — is not one a control can act on differently.
 */
export function declaredReferences(manifest: Manifest | undefined, typeName: string): ManifestReference[] {
  return typeOf(manifest, typeName)?.references ?? []
}

/** One declared edge, with what the node holds on it. */
export interface ReferenceEdge {
  /** The declaration, as the manifest states it. */
  declares: ManifestReference
  /** The references on this edge, in the order they were added. */
  held: Reference[]
  /** Whether another may be added under this declaration. */
  open: boolean
}

/**
 * Join a node's references to the edges its type declares.
 *
 * **A declared name wins over a family**, which is the core's rule rather
 * than a reading invented here: a type declaring `input` and a family of
 * authored names still means `input` when a reference says it, and the
 * family answers for every name left over. Applying it in the other
 * order would file a placement's input under its bindings the moment a
 * declaration happened to name one `input`.
 *
 * A family is always open. `multiple` still governs each *name* within it
 * — a second `feature` is refused where the family is single — but a new
 * name is always addable, which is what makes the family a family.
 */
export function referenceEdges(
  declared: ManifestReference[],
  held: Reference[] | undefined,
): ReferenceEdge[] {
  const references = held ?? []
  const named = new Set(declared.filter((edge) => !edge.authoredNames).map((edge) => edge.name))

  return declared.map((declares) => {
    const on = declares.authoredNames
      ? references.filter((reference) => !named.has(reference.name))
      : references.filter((reference) => reference.name === declares.name)
    return { declares, held: on, open: declares.authoredNames || declares.multiple || on.length === 0 }
  })
}

/**
 * References the type's declarations do not account for.
 *
 * Always empty while a type and its documents agree — the write path
 * refuses a name the type does not declare. It stops being empty when a
 * declaration is narrowed under documents already written, and then this
 * is the only way an author can see one, let alone remove it. The same
 * job `unarranged` does for a child nothing places.
 */
export function unaccountedReferences(
  declared: ManifestReference[],
  held: Reference[] | undefined,
): Reference[] {
  if (declared.some((edge) => edge.authoredNames)) return []
  const named = new Set(declared.map((edge) => edge.name))
  return (held ?? []).filter((reference) => !named.has(reference.name))
}

/**
 * What an edge accepts, in one line an author can read.
 *
 * The roles when the edge declares them, because "any record" is what the
 * author is choosing among and the resolved list is how that was spelled
 * out — seven type names where one word was meant. The names otherwise,
 * which is the ordinary case and already short.
 */
export function acceptsLabel(declares: ManifestReference): string {
  if (declares.acceptsRoles?.length) {
    return declares.acceptsRoles.map((role: string) => `any ${role}`).join(' or ')
  }
  if (declares.accepts.length === 0) return 'nothing in this tenant'
  return declares.accepts.join(', ')
}

/**
 * An edge id for a new reference: readable, and unique on this node.
 *
 * Derived from the name the reference plays under rather than from the
 * target, which is the opposite of what a link id does and follows from
 * what each identifies. A link id names a *placement*, and two placements
 * of one component are told apart by nothing but their targets; an edge
 * id names an association, whose whole meaning is the role — so `input`
 * and `input-2` read as an author thinks of them, where `practice-cloud`
 * would say which record was chosen at a moment nobody will remember.
 *
 * Unique among this node's own references, which is what the surface
 * requires: an edge id is scoped to the node that holds it.
 */
export function mintEdgeId(name: string, taken: Set<string>): string {
  const base = name.replace(/[^a-zA-Z0-9-]/g, '-').slice(0, 24) || 'ref'
  if (!taken.has(base)) return base
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${base}-${suffix}`
    if (!taken.has(candidate)) return candidate
  }
}
